import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronDown, ChevronUp, ExternalLink, Pencil, Phone, PhoneCall, FileText,
  FolderKanban, CalendarClock, CheckSquare, AlertTriangle, Loader2, Clock, Building2,
  StickyNote, Eye,
} from 'lucide-react';
import { format, subDays, isSameDay } from 'date-fns';

const ORANGE = '#ee7203';
const LIME = '#65a30d';
const BLUE = '#2563eb';
const PURPLE = '#9333ea';
const JADE = '#00966d';
const TEAL = '#0891b2';

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const toDateStr = (d) => d.toISOString().split('T')[0];
const toISO = (d) => toDateStr(d) + 'T00:00:00';
const toISOEnd = (d) => toDateStr(d) + 'T23:59:59';
const fmtTime = (iso) => { try { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; } catch { return ''; } };
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }); } catch { return iso; } };

/** Every activity/to-do row is now fully clickable (onRowClick) — the small
 * "Update" button and the "CRM" external link remain as secondary actions and
 * stop propagation so they don't double-fire the row click. */
function ActionLine({ color, icon: Icon, title, subtitle, meta, customerName, onCustomerClick, crmUrl, onEdit, editLabel = 'Update', onRowClick }) {
  const clickable = !!onRowClick;
  return (
    <div
      onClick={clickable ? onRowClick : undefined}
      className={`flex items-start gap-2.5 rounded-xl border bg-white pl-3 pr-2.5 py-2 border-l-4 shadow-sm transition-colors ${clickable ? 'cursor-pointer hover:bg-gray-50 hover:border-l-[5px]' : ''}`}
      style={{ borderLeftColor: color, borderColor: '#f1f5f9' }}
      data-testid="activity-hub-row"
    >
      <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${color}18` }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-gray-900 truncate">{title}</p>
          {meta}
        </div>
        {customerName && (
          <button onClick={(e) => { e.stopPropagation(); onCustomerClick?.(); }} className="text-xs font-semibold text-gray-600 hover:text-orange-600 hover:underline truncate block" title="Open Manage Customer">
            {customerName}
          </button>
        )}
        {subtitle && <p className="text-xs text-gray-400 truncate mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {onEdit && (
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="text-[11px] font-semibold flex items-center gap-1 px-2 py-1 rounded-full hover:opacity-80 transition-opacity" style={{ background: `${color}18`, color }}>
            <Pencil className="w-3 h-3" /> {editLabel}
          </button>
        )}
        {crmUrl && (
          <a href={crmUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[11px] text-blue-500 hover:underline flex items-center gap-0.5">
            <ExternalLink className="w-3 h-3" />CRM
          </a>
        )}
        {clickable && <Eye className="w-3 h-3 text-gray-300" />}
      </div>
    </div>
  );
}

function OverdueBar({ count, expanded, onToggle, children }) {
  if (count === 0) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
        <span className="text-xs font-bold text-amber-700 flex-1">{count} overdue</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-amber-500" /> : <ChevronDown className="w-3.5 h-3.5 text-amber-500" />}
      </button>
      {expanded && <div className="px-2.5 pb-2.5 space-y-1.5">{children}</div>}
    </div>
  );
}

async function fetchMemberActivity({ API, authHeaders, memberId, windowFrom, windowTo, tasksFrom }) {
  const from = encodeURIComponent(toISO(windowFrom));
  const to = encodeURIComponent(toISOEnd(windowTo));
  const tasksFromParam = encodeURIComponent(toISO(tasksFrom));
  const headers = authHeaders();
  const safeJson = async (p) => { try { const r = await p; if (!r.ok) return {}; return await r.json(); } catch { return {}; } };
  const [crmQuotes, localQuotes, crmProjects, crmTasks, crmNotes, eight] = await Promise.all([
    safeJson(fetch(`${API}/team-management/crm-quotes/${memberId}?from_date=${from}&to_date=${to}`, { headers })),
    safeJson(fetch(`${API}/team-management/local-quotes/${memberId}?from_date=${from}&to_date=${to}`, { headers })),
    safeJson(fetch(`${API}/team-management/crm-projects/${memberId}?from_date=${from}&to_date=${to}`, { headers })),
    safeJson(fetch(`${API}/team-management/crm-tasks/${memberId}?from_date=${tasksFromParam}&to_date=${to}`, { headers })),
    safeJson(fetch(`${API}/team-management/crm-notes/${memberId}?from_date=${from}&to_date=${to}`, { headers })),
    safeJson(fetch(`${API}/8x8/calls-history?from_date=${toDateStr(windowFrom)}&to_date=${toDateStr(windowTo)}&user_id=${memberId}`, { headers })),
  ]);
  return {
    crmQuotes: crmQuotes.quotes || [],
    localQuotes: localQuotes.quotes || [],
    crmProjects: crmProjects.projects || [],
    crmTasks: crmTasks.tasks || [],
    crmNotes: crmNotes.notes || [],
    eightXEight: eight.calls || [],
  };
}

function dedupeLocalQuotes(localQuotes, crmQuotes) {
  const crmOrderNumbers = new Set(crmQuotes.map((q) => (q.order_number || q.subject || '').trim()).filter(Boolean));
  return localQuotes.filter((q) => !crmOrderNumbers.has((q.order_number || '').trim()));
}

function buildActionEntries(data, from, to) {
  const inRange = (dateVal) => { if (!dateVal) return false; const d = new Date(dateVal); return d >= from && d <= to; };
  const entries = [];

  data.eightXEight.forEach((c) => {
    if (!inRange(c.created_at || c.start_time)) return;
    entries.push({
      key: `8x8-${c.id}`, color: LIME, icon: PhoneCall, sortAt: c.created_at || c.start_time,
      title: c.direction === 'inbound' ? 'Incoming call' : 'Outbound call',
      subtitle: `${fmtTime(c.created_at || c.start_time)}${c.duration_seconds ? ` · ${Math.round(c.duration_seconds / 60)} min` : ''}`,
      customerName: c.account_name || c.phone_number || null,
      raw: c, kind: 'eight',
    });
  });

  (data.crmCalls || []).forEach((c) => {
    entries.push({
      key: `crmcall-${c.id}`, color: BLUE, icon: Phone, sortAt: c.start_time,
      title: c.subject || 'Call logged to CRM',
      subtitle: [c.business_unit, c.purpose].filter(Boolean).join(' · ') || fmtTime(c.start_time),
      customerName: c.account_name || c.contact_name || null,
      crmUrl: c.crm_url, raw: c, kind: 'crmCall',
    });
  });

  data.crmQuotes.forEach((q) => {
    if (!inRange(q.created_at || q.closing_date)) return;
    entries.push({
      key: `crmq-${q.id}`, color: BLUE, icon: FileText, sortAt: q.created_at,
      title: q.subject || 'CRM Quote',
      subtitle: q.value != null ? fmtMoney(q.value) : (q.stage || null),
      customerName: q.account_name || null,
      crmUrl: q.crm_url, raw: q, kind: 'crmQuote',
    });
  });

  dedupeLocalQuotes(data.localQuotes, data.crmQuotes).forEach((q) => {
    if (!inRange(q.parsed_at)) return;
    entries.push({
      key: `lq-${q.id}`, color: ORANGE, icon: FileText, sortAt: q.parsed_at,
      title: q.order_number ? `Quote #${q.order_number}` : 'Quote',
      subtitle: q.orders_value > 0 ? fmtMoney(q.orders_value) : null,
      customerName: q.customer_name || null,
      raw: q, kind: 'localQuote',
    });
  });

  data.crmProjects.forEach((p) => {
    if (!inRange(p.created_at)) return;
    entries.push({
      key: `proj-${p.id}`, color: JADE, icon: FolderKanban, sortAt: p.created_at,
      title: p.name || 'Project',
      subtitle: p.value != null ? fmtMoney(p.value) : (p.stage || null),
      customerName: p.account_name || null,
      crmUrl: p.crm_url, raw: p, kind: 'project',
    });
  });

  (data.crmTasks || []).forEach((t) => {
    if (!inRange(t.modified_time)) return;
    entries.push({
      key: `tku-${t.id}`, color: PURPLE, icon: CheckSquare, sortAt: t.modified_time,
      title: t.subject ? `Task: ${t.subject}` : 'Task updated',
      subtitle: [t.status, t.due_date ? `Due ${t.due_date}` : null].filter(Boolean).join(' · '),
      customerName: t.account_name || t.linked_account_name || null,
      crmUrl: t.crm_url, raw: t, kind: 'task',
    });
  });

  (data.crmNotes || []).forEach((n) => {
    if (!inRange(n.created_at)) return;
    entries.push({
      key: `note-${n.id}`, color: TEAL, icon: StickyNote, sortAt: n.created_at,
      title: n.title || 'Note added',
      subtitle: n.content || null,
      customerName: n.parent_name || null,
      crmUrl: n.crm_url, raw: n, kind: 'note',
    });
  });

  return entries.sort((a, b) => new Date(b.sortAt || 0) - new Date(a.sortAt || 0));
}

function ToDoSection({ tasks, scheduledCalls, todayStr, onOpenCustomer, onEditCall, onOpenTask }) {
  const [overdueOpen, setOverdueOpen] = useState(false);
  const isOverdueTask = (t) => t.due_date && t.due_date < todayStr && t.status !== 'Completed';
  const isTodayTask = (t) => t.due_date === todayStr && t.status !== 'Completed';
  const isOverdueCall = (c) => c.start_time && c.start_time.slice(0, 10) < todayStr;
  const isTodayCall = (c) => c.start_time && isSameDay(new Date(c.start_time), new Date(todayStr));

  const todayTasks = tasks.filter(isTodayTask);
  const overdueTasks = tasks.filter(isOverdueTask);
  const todayCalls = scheduledCalls.filter(isTodayCall);
  const overdueCalls = scheduledCalls.filter(isOverdueCall);
  const overdueCount = overdueTasks.length + overdueCalls.length;

  if (todayTasks.length === 0 && todayCalls.length === 0 && overdueCount === 0) {
    return <p className="text-xs text-gray-400 italic px-1">Nothing to do today 🎉</p>;
  }

  return (
    <div className="space-y-1.5">
      {todayCalls.map((c) => (
        <ActionLine key={`sc-${c.id}`} color={PURPLE} icon={CalendarClock} title={c.subject || 'Scheduled call'} subtitle={fmtTime(c.start_time)}
          customerName={c.account_name || c.contact_name} onCustomerClick={() => onOpenCustomer({ id: c.account_id, name: c.account_name })}
          crmUrl={c.crm_url} onEdit={() => onEditCall(c)} onRowClick={() => onEditCall(c)} />
      ))}
      {todayTasks.map((t) => (
        <ActionLine key={`tk-${t.id}`} color={PURPLE} icon={CheckSquare} title={t.subject || 'Task'}
          subtitle={[t.status, t.priority === 'High' ? 'High priority' : null].filter(Boolean).join(' · ')}
          customerName={t.account_name || t.linked_account_name} onCustomerClick={() => (t.account_name || t.linked_account_name) && onOpenCustomer({ id: t.account?.id, name: t.account_name || t.linked_account_name })}
          crmUrl={t.crm_url} onRowClick={() => onOpenTask(t)} />
      ))}
      <OverdueBar count={overdueCount} expanded={overdueOpen} onToggle={() => setOverdueOpen((v) => !v)}>
        {overdueCalls.map((c) => (
          <ActionLine key={`osc-${c.id}`} color={PURPLE} icon={CalendarClock} title={c.subject || 'Scheduled call'} subtitle={`${fmtDay(c.start_time)} — overdue`}
            customerName={c.account_name || c.contact_name} onCustomerClick={() => onOpenCustomer({ id: c.account_id, name: c.account_name })}
            crmUrl={c.crm_url} onEdit={() => onEditCall(c)} onRowClick={() => onEditCall(c)} />
        ))}
        {overdueTasks.map((t) => (
          <ActionLine key={`otk-${t.id}`} color={PURPLE} icon={CheckSquare} title={t.subject || 'Task'} subtitle={`Due ${t.due_date} — overdue`}
            customerName={t.account_name || t.linked_account_name} onCustomerClick={() => (t.account_name || t.linked_account_name) && onOpenCustomer({ id: t.account?.id, name: t.account_name || t.linked_account_name })}
            crmUrl={t.crm_url} onRowClick={() => onOpenTask(t)} />
        ))}
      </OverdueBar>
    </div>
  );
}

function rowClickFor(e, { onEditCall, onOpenCall, onOpenLocalItem, onOpenTask }) {
  switch (e.kind) {
    case 'crmCall': return () => onEditCall(e.raw);
    case 'eight': return () => onOpenCall(e.raw);
    case 'localQuote': return () => onOpenLocalItem(e.raw);
    case 'task': return () => onOpenTask(e.raw);
    default: return e.crmUrl ? () => window.open(e.crmUrl, '_blank', 'noopener') : undefined; // crmQuote / project / note
  }
}

function ActionsFeed({ entries, onOpenCustomer, onEditCall, onOpenCall, onOpenLocalItem, onOpenTask, emptyLabel }) {
  if (entries.length === 0) return <p className="text-xs text-gray-400 italic px-1">{emptyLabel}</p>;
  return (
    <div className="space-y-1.5">
      {entries.map((e) => (
        <ActionLine key={e.key} color={e.color} icon={e.icon} title={e.title} subtitle={e.subtitle}
          customerName={e.customerName}
          onCustomerClick={() => e.customerName && onOpenCustomer({ id: e.raw?.account_id, name: e.customerName })}
          crmUrl={e.crmUrl}
          onEdit={e.kind === 'crmCall' ? () => onEditCall(e.raw) : undefined}
          onRowClick={rowClickFor(e, { onEditCall, onOpenCall, onOpenLocalItem, onOpenTask })} />
      ))}
    </div>
  );
}

function MemberBlock({ name, loading, data, crmCallsToday, crmCallsWeek, scheduledCallsAll, todayStr, weekFrom, weekTo, onOpenCustomer, onEditCall, onOpenCall, onOpenLocalItem, onOpenTask, showHeader }) {
  const todayEntries = useMemo(
    () => buildActionEntries({ ...data, crmCalls: crmCallsToday }, new Date(todayStr), new Date(todayStr + 'T23:59:59')),
    [data, crmCallsToday, todayStr]
  );
  const scheduledToday = scheduledCallsAll;

  return (
    <div className="space-y-3">
      {showHeader && (
        <div className="flex items-center gap-2 pt-1">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: ORANGE }}>
            {(name || '?').slice(0, 1).toUpperCase()}
          </div>
          <p className="text-sm font-extrabold text-gray-800">{name}</p>
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading activity…</div>
      ) : (
        <>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 px-1">Today's Tasks</p>
            <ToDoSection tasks={data.crmTasks} scheduledCalls={scheduledToday} todayStr={todayStr} onOpenCustomer={onOpenCustomer} onEditCall={onEditCall} onOpenTask={onOpenTask} />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 px-1">Today's Actions</p>
            <ActionsFeed entries={todayEntries} onOpenCustomer={onOpenCustomer} onEditCall={onEditCall} onOpenCall={onOpenCall} onOpenLocalItem={onOpenLocalItem} onOpenTask={onOpenTask} emptyLabel="Nothing done today yet" />
          </div>
        </>
      )}
    </div>
  );
}

export default function SalesActivityHub({
  user, hubMode, effectiveMemberId, effectiveMemberName, isAllView, tabOrder, memberName,
  dateMode, dateRange, authHeaders, API, calls, onOpenCustomer, onEditCall, onOpenCall, onOpenLocalItem, onOpenTask,
}) {
  const todayStr = toDateStr(new Date());
  const singlePeriodMode = dateMode !== 'day';
  const anchorDay = dateRange.from;
  const weekFrom = subDays(anchorDay, 6);
  const weekTo = anchorDay;
  const periodFrom = singlePeriodMode ? dateRange.from : weekFrom;
  const periodTo = singlePeriodMode ? dateRange.to : weekTo;
  const tasksFrom = subDays(periodFrom, 60);

  const memberIds = isAllView ? tabOrder : [effectiveMemberId];

  const [dataByMember, setDataByMember] = useState({});
  const [loadingIds, setLoadingIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoadingIds(new Set(memberIds));
    const results = await Promise.all(
      memberIds.map((id) =>
        fetchMemberActivity({ API, authHeaders, memberId: id, windowFrom: periodFrom, windowTo: periodTo, tasksFrom }).then((d) => [id, d])
      )
    );
    setDataByMember(Object.fromEntries(results));
    setLoadingIds(new Set());
  }, [memberIds.join(','), toDateStr(periodFrom), toDateStr(periodTo)]);

  useEffect(() => { load(); }, [load]);

  const callsForMember = (uid, from, to, scheduledOnly) => {
    const name = (memberName(uid) || '').toLowerCase();
    return (calls || []).filter((c) => {
      if ((c.owner_name || '').toLowerCase() !== name) return false;
      const isScheduled = (c.call_status || '').toLowerCase() === 'scheduled';
      if (scheduledOnly !== undefined && isScheduled !== scheduledOnly) return false;
      const d = c.start_time ? new Date(c.start_time) : null;
      if (!d) return false;
      return d >= from && d <= to;
    });
  };

  const wideStart = subDays(new Date(todayStr), 60);
  const wideEnd = new Date(todayStr + 'T23:59:59');

  const emptyMemberData = { crmQuotes: [], localQuotes: [], crmProjects: [], crmTasks: [], crmNotes: [], eightXEight: [] };

  return (
    <div className="space-y-5" data-testid="sales-activity-hub">
      {isAllView ? (
        tabOrder.map((uid, idx) => {
          const data = dataByMember[uid] || emptyMemberData;
          return (
            <div key={uid} className={idx > 0 ? 'pt-3 border-t border-gray-100' : ''}>
              <MemberBlock name={memberName(uid)} loading={loadingIds.has(uid)} data={data}
                crmCallsToday={callsForMember(uid, new Date(todayStr), new Date(todayStr + 'T23:59:59'), false)}
                crmCallsWeek={callsForMember(uid, periodFrom, periodTo, false)}
                scheduledCallsAll={callsForMember(uid, wideStart, wideEnd, true)}
                todayStr={todayStr} weekFrom={periodFrom} weekTo={periodTo}
                onOpenCustomer={onOpenCustomer} onEditCall={onEditCall} onOpenCall={onOpenCall} onOpenLocalItem={onOpenLocalItem} onOpenTask={onOpenTask} showHeader />
            </div>
          );
        })
      ) : (
        <MemberBlock name={effectiveMemberName} loading={loadingIds.has(effectiveMemberId)}
          data={dataByMember[effectiveMemberId] || emptyMemberData}
          crmCallsToday={callsForMember(effectiveMemberId, new Date(todayStr), new Date(todayStr + 'T23:59:59'), false)}
          crmCallsWeek={callsForMember(effectiveMemberId, periodFrom, periodTo, false)}
          scheduledCallsAll={callsForMember(effectiveMemberId, wideStart, wideEnd, true)}
          todayStr={todayStr} weekFrom={periodFrom} weekTo={periodTo}
          onOpenCustomer={onOpenCustomer} onEditCall={onEditCall} onOpenCall={onOpenCall} onOpenLocalItem={onOpenLocalItem} onOpenTask={onOpenTask} showHeader={false} />
      )}

      <div className="pt-3 border-t border-gray-200">
        <div className="flex items-center gap-1.5 mb-2 px-1">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            {singlePeriodMode ? `Activity — ${format(dateRange.from, 'dd MMM')} to ${format(dateRange.to, 'dd MMM yyyy')}` : `This Week (${format(weekFrom, 'dd MMM')} – ${format(weekTo, 'dd MMM')})`}
          </p>
        </div>
        {isAllView ? (
          tabOrder.map((uid) => {
            const data = dataByMember[uid] || emptyMemberData;
            const entries = buildActionEntries({ ...data, crmCalls: callsForMember(uid, periodFrom, periodTo, false) }, periodFrom, periodTo);
            return (
              <div key={uid} className="mb-3">
                <p className="text-xs font-bold text-gray-600 mb-1.5 px-1">{memberName(uid)}</p>
                <ActionsFeed entries={entries} onOpenCustomer={onOpenCustomer} onEditCall={onEditCall} onOpenCall={onOpenCall} onOpenLocalItem={onOpenLocalItem} onOpenTask={onOpenTask} emptyLabel="No activity this period" />
              </div>
            );
          })
        ) : (
          <ActionsFeed
            entries={buildActionEntries(
              { ...(dataByMember[effectiveMemberId] || emptyMemberData), crmCalls: callsForMember(effectiveMemberId, periodFrom, periodTo, false) },
              periodFrom, periodTo
            )}
            onOpenCustomer={onOpenCustomer} onEditCall={onEditCall} onOpenCall={onOpenCall} onOpenLocalItem={onOpenLocalItem} onOpenTask={onOpenTask} emptyLabel="No activity this period" />
        )}
      </div>
    </div>
  );
}
